import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { Pagination, Navigation } from "swiper";
import { Swiper as SwiperRef } from "swiper/types";
import { Swiper, SwiperSlide } from "swiper/react";
import { IGif } from "@/common/services/types";
import { TapTracker } from "@/components/UI/TapTracker";
import { GalleryGifNav } from "../GalleryGifNav";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "./GalleryGif.scss";
import { dateTimeHR } from "@/common/utils/dates";

interface IGalleryGifProps {
  gallery: IGif[];
  initialGif: IGif;
  onSetGalleryGif: (gif: IGif) => void;
  onLike: () => void;
  isVisible: boolean;
  sendGifView: (gif: IGif) => void;
}

export const GalleryGif = memo((props: IGalleryGifProps) => {
  const {
    onSetGalleryGif,
    gallery,
    initialGif,
    onLike,
    isVisible,
    sendGifView,
  } = props;

  const [swiper, setSwiper] = useState<SwiperRef | null>(null);

  const slidePrevArrow = useRef<HTMLButtonElement>(null);
  const slideNextArrow = useRef<HTMLButtonElement>(null);
  const sliderPagination = useRef<HTMLDivElement | null>(null);

  const navigationSettings = {
    prevEl: slidePrevArrow.current,
    nextEl: slideNextArrow.current
  };

  useEffect(() => {
    if (isVisible) {
      sendGifView(initialGif);
    }
  }, [isVisible]);

  useEffect(() => {
    if (swiper) {
      if (swiper.params.navigation && typeof swiper.params.navigation !== "boolean") {
        swiper.params.navigation.prevEl = slidePrevArrow.current;
        swiper.params.navigation.nextEl = slideNextArrow.current;
      }

      onSetGalleryGif(gallery[swiper.activeIndex]);
    }
  }, [swiper, gallery, onSetGalleryGif]);

  const paginationSettings = {
    el: sliderPagination.current,
    clickable: true,
    renderBullet: (index: number, className: string) => {
      return `<div class="dot ${className}"></div>`;
    },
  };

  const sendMetrics = useCallback((index: number) => {
    if (index === 0) {
      return;
    }

    if (!gallery[index]) {
      return;
    }

    sendGifView(gallery[index]);
  }, [gallery]);

  const handleSlideChange = useCallback((swiper: SwiperRef) => {
    sendMetrics(swiper.activeIndex);
    onSetGalleryGif(gallery[swiper.activeIndex]);
  }, [sendMetrics, gallery, onSetGalleryGif]);

  const initialSlide = useMemo(() => {
    const foundIndex = gallery.findIndex(({ id }) => id === initialGif.id);

    if (foundIndex === -1) {
      return 0;
    }

    return foundIndex;
  }, [initialGif, gallery]);

  const renderSlide = (item: IGif) => (
    <SwiperSlide key={item.id}>
      <div className="GalleryGifV2-ImageWrap">
        <img
          src={item.urls.hd ?? item.urls.sd}
          className="GalleryGifV2-Image"
          alt={`${item.tags.slice(0, 3).join(", ")} porn ${item.id} uploaded by ${item.userName} on ${dateTimeHR(item.createDate)}`}
          style={{
            aspectRatio: `${item.width} / ${item.height}`,
          }}
        />
      </div>
    </SwiperSlide>
  );

  const renderSlides = gallery.map(renderSlide);

  const handleTapAction = useCallback((clicks: number) => {
    if (clicks === 2) {
      onLike();
    }
  }, [onLike]);

  return (
    <>
      <TapTracker onTap={handleTapAction}>
        <div className="GalleryGifV2">
          <Swiper
            slidesPerView={1}
            modules={[Navigation, Pagination]}
            navigation={navigationSettings}
            pagination={paginationSettings}
            onSwiper={setSwiper}
            onSlideChange={handleSlideChange}
            initialSlide={initialSlide}
          >
            {renderSlides}
          </Swiper>
        </div>
      </TapTracker>
      <GalleryGifNav
        slidePrevArrow={slidePrevArrow}
        sliderPagination={sliderPagination}
        slideNextArrow={slideNextArrow}
      />
    </>
  );
});

